import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { API_URL } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { formatMessage } from "../locales";

function VerseChat({
  verse,
  bookName,
  chapterNumber,
  messages,
  setMessages,
  input,
  setInput,
  isLoading,
  setIsLoading,
  fontType,
}) {
  const messagesEndRef = useRef(null);
  const { messages: langMessages } = useLanguage();
  const [shouldScroll, setShouldScroll] = useState(false);
  const [chatId, setChatId] = useState(null);

  // Font family mapping
  const fontFamilyMap = {
    default: undefined,
    opendyslexic: "OpenDyslexic, Arial, sans-serif",
    serif: "Georgia, Times New Roman, serif",
    sans: "Arial, Helvetica, sans-serif",
    mono: "Courier New, monospace",
    merriweather: "Merriweather, Georgia, serif",
    lato: "Lato, Arial, sans-serif",
  };

  // Get suggestions from localization
  const suggestions = langMessages?.bible?.chat?.suggestions || [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput("");
    const newMessage = { role: "user", content: userMessage };
    setMessages((prev) => [...prev, newMessage]);
    setIsLoading(true);
    setShouldScroll(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          verse,
          bookName,
          chapterNumber,
          conversationHistory: messages
            .concat(newMessage)
            .filter((msg) => msg.role === "user" || msg.role === "assistant"),
          chatId,
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = { role: "assistant", content: "" };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n").filter(Boolean);

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = JSON.parse(line.slice(6));
            if (data.done) {
              if (!chatId && data.chatId) {
                setChatId(data.chatId);
              }
              break;
            }
            if (data.content) {
              assistantMessage.content += data.content;
              setMessages((prev) => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage && lastMessage.role === "assistant") {
                  lastMessage.content = assistantMessage.content;
                } else {
                  newMessages.push({ ...assistantMessage });
                }
                return newMessages;
              });
            }
          }
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "I apologize, but I encountered an error. Please try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInput("");
    handleSendSuggestion(suggestion);
  };

  const handleSendSuggestion = async (suggestion) => {
    if (!suggestion.trim()) return;
    const userMessage = suggestion.trim();
    const newMessage = { role: "user", content: userMessage };
    setMessages((prev) => [...prev, newMessage]);
    setIsLoading(true);
    setShouldScroll(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          verse,
          bookName,
          chapterNumber,
          conversationHistory: messages
            .concat(newMessage)
            .filter((msg) => msg.role === "user" || msg.role === "assistant"),
          chatId,
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = { role: "assistant", content: "" };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n").filter(Boolean);

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = JSON.parse(line.slice(6));
            if (data.done) {
              if (!chatId && data.chatId) {
                setChatId(data.chatId);
              }
              break;
            }
            if (data.content) {
              assistantMessage.content += data.content;
              setMessages((prev) => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage && lastMessage.role === "assistant") {
                  lastMessage.content = assistantMessage.content;
                } else {
                  newMessages.push({ ...assistantMessage });
                }
                return newMessages;
              });
            }
          }
        }
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "I apologize, but I encountered an error. Please try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      shouldScroll &&
      messages.length > 0 &&
      messages[messages.length - 1].role === "user"
    ) {
      scrollToBottom();
      setShouldScroll(false);
    }
  }, [messages, shouldScroll]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          overflow: "auto",
          mb: 2,
          minHeight: 0,
          bgcolor: "transparent",
          fontFamily: fontFamilyMap[fontType],
        }}
      >
        {messages.map((message, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent:
                message.role === "user" ? "flex-end" : "flex-start",
              mb: 2,
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 2,
                maxWidth: "80%",
                bgcolor:
                  message.role === "user" ? "primary.main" : "background.paper",
                color:
                  message.role === "user"
                    ? "primary.contrastText"
                    : "text.primary",
                fontFamily: fontFamilyMap[fontType],
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: "pre-wrap",
                  fontFamily: fontFamilyMap[fontType],
                }}
              >
                {message.content}
              </Typography>
            </Paper>
          </Box>
        ))}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Paper>

      {/* Suggestion Boxes */}
      {suggestions.length > 0 && messages.length === 0 && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
          {suggestions.map((suggestion, idx) => (
            <Paper
              key={idx}
              elevation={2}
              sx={{
                p: 1.2,
                px: 2,
                cursor: "pointer",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                transition: "background 0.2s",
                "&:hover": {
                  bgcolor: "primary.light",
                  color: "primary.contrastText",
                },
                fontFamily: fontFamilyMap[fontType],
              }}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: fontFamilyMap[fontType] }}
              >
                {suggestion}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={formatMessage(langMessages, "bible.chat.placeholder")}
          variant="outlined"
          size="small"
          InputProps={{
            style: { fontFamily: fontFamilyMap[fontType] },
          }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

export default VerseChat;
